import { useState, useCallback } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { useTranslation } from "../../i18n";
import {
  useSegmentDefinitions,
  useCreateSegment,
  useDeleteSegment,
} from "../../features/super/hooks/useCampaigns";
import toast from "react-hot-toast";

/**
 * Manager for operator-defined snapshot segments.
 *
 * The seven built-in segments are computed in code and cannot be tuned. This
 * lets an operator describe an extra segment with a few structured
 * predicates — "inactive 90+ days and 500–2000 minutes on the balance" — which
 * the snapshot step then materialises alongside the built-ins, so it becomes
 * selectable as `Имя сегмента в snapshot`.
 *
 * There is deliberately no free-form SQL field: the backend accepts only these
 * four columns and binds every value as a query parameter. Anything more
 * expressive still has to go through the database directly, which the help
 * popover below documents.
 */
export default function SegmentManager({ campaignId = null }) {
  const { t } = useTranslation();
  const { data, isLoading } = useSegmentDefinitions(campaignId);
  const createMutation = useCreateSegment();
  const deleteMutation = useDeleteSegment();
  const [showHelp, setShowHelp] = useState(false);
  const [form, setForm] = useState({
    name: "",
    minInactiveDays: 30,
    minBalance: "",
    maxBalance: "",
  });

  const definitions = data?.items ?? [];

  const reset = useCallback(() => {
    setForm({ name: "", minInactiveDays: 30, minBalance: "", maxBalance: "" });
  }, []);

  const parseOptionalInt = (raw) => {
    const trimmed = String(raw).trim();
    if (trimmed === "") return null;
    const n = Number(trimmed);
    return Number.isFinite(n) ? Math.trunc(n) : null;
  };

  const handleCreate = async () => {
    if (!form.name.trim()) {
      toast.error(t("campaign.segmentNameRequired"));
      return;
    }
    try {
      await createMutation.mutateAsync({
        name: form.name.trim(),
        campaignId,
        minInactiveDays: Number(form.minInactiveDays) || 0,
        minBalance: parseOptionalInt(form.minBalance),
        maxBalance: parseOptionalInt(form.maxBalance),
      });
      toast.success(t("campaign.segmentCreated"));
      reset();
    } catch (err) {
      toast.error(describe(err, t("campaign.segmentCreateFailed")));
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteMutation.mutateAsync(id);
      toast.success(t("campaign.segmentDeleted"));
    } catch (err) {
      toast.error(describe(err, t("campaign.segmentDeleteFailed")));
    }
  };

  return (
    <div className="border border-c-gray-400/30 rounded-lg p-3 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">{t("campaign.customSegments")}</h3>
        <button
          type="button"
          onClick={() => setShowHelp(!showHelp)}
          aria-expanded={showHelp}
          aria-label={t("campaign.segmentHelp")}
          title={t("campaign.segmentHelp")}
          className="w-5 h-5 rounded-full border border-c-gray-400/50 text-c-gray-300 text-xs leading-none flex items-center justify-center hover:text-white"
        >
          ?
        </button>
      </div>

      {showHelp && (
        <div className="bg-c-gray-500/60 border border-c-gray-400/20 rounded-lg p-3 text-xs text-c-gray-300 space-y-2">
          <p>{t("campaign.segmentHelpIntro")}</p>
          <p className="font-medium text-white">
            {t("campaign.segmentHelpDbTitle")}
          </p>
          <pre className="bg-c-gray-600/60 rounded p-2 overflow-x-auto text-[11px] leading-relaxed">
            {`INSERT INTO campaign_segment_snapshot
  (campaign_id, customer_id, segment_name, snapshot_date)
VALUES (:campaign_id, :customer_id, :segment_name, CURRENT_DATE);

-- unique on (campaign_id, segment_name,
--            snapshot_date, customer_id)`}
          </pre>
          <p>{t("campaign.segmentHelpDbNote")}</p>
        </div>
      )}

      {isLoading ? (
        <p className="text-c-gray-400 text-xs">{t("common.loading")}</p>
      ) : definitions.length === 0 ? (
        <p className="text-c-gray-400 text-xs">
          {t("campaign.noCustomSegments")}
        </p>
      ) : (
        <ul className="space-y-1">
          {definitions.map((d) => (
            <li
              key={d.id}
              className="flex items-center justify-between gap-2 text-xs bg-c-gray-500/40 rounded px-2 py-1"
            >
              <span className="min-w-0 truncate">
                <code className="text-accent">{d.name}</code>
                <span className="text-c-gray-400"> — {describeDef(d, t)}</span>
                {d.isGlobal && (
                  <span className="ml-1 text-c-gray-500">
                    ({t("campaign.segmentGlobal")})
                  </span>
                )}
              </span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => handleDelete(d.id)}
                disabled={deleteMutation.isPending}
                aria-label={t("campaign.segmentDelete")}
              >
                ✕
              </Button>
            </li>
          ))}
        </ul>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 items-end">
        <div>
          <Label className="text-xs">{t("campaign.segmentName")}</Label>
          <Input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="vip_sleeping_90d"
            disabled={createMutation.isPending}
          />
        </div>
        <div>
          <Label className="text-xs">{t("campaign.segmentInactiveDays")}</Label>
          <Input
            type="number"
            value={form.minInactiveDays}
            onChange={(e) =>
              handleNumber(e, (n) => setForm({ ...form, minInactiveDays: n }))
            }
            disabled={createMutation.isPending}
          />
        </div>
        <div>
          <Label className="text-xs">{t("campaign.segmentMinBalance")}</Label>
          <Input
            value={form.minBalance}
            onChange={(e) => setForm({ ...form, minBalance: e.target.value })}
            placeholder="—"
            disabled={createMutation.isPending}
          />
        </div>
        <div>
          <Label className="text-xs">{t("campaign.segmentMaxBalance")}</Label>
          <Input
            value={form.maxBalance}
            onChange={(e) => setForm({ ...form, maxBalance: e.target.value })}
            placeholder="—"
            disabled={createMutation.isPending}
          />
        </div>
      </div>

      <div className="flex justify-between items-center gap-2">
        <p className="text-c-gray-500 text-xs">
          {campaignId == null
            ? t("campaign.segmentScopeGlobal")
            : t("campaign.segmentScopeCampaign")}
        </p>
        <Button
          size="sm"
          variant="secondary"
          onClick={handleCreate}
          disabled={createMutation.isPending}
        >
          {t("campaign.segmentAdd")}
        </Button>
      </div>
    </div>
  );
}

/** Mirror of the leading-zero guard used by CampaignForm. */
function handleNumber(event, commit) {
  const raw = event.target.value;
  if (raw === "" || raw === "-") {
    event.target.value = "";
    commit(0);
    return;
  }
  const parsed = Number(raw);
  if (Number.isNaN(parsed)) {
    event.target.value = "";
    commit(0);
    return;
  }
  event.target.value = String(parsed);
  commit(parsed);
}

function describeDef(d, t) {
  const parts = [];
  if (d.minInactiveDays > 0) {
    parts.push(t("campaign.segmentInactiveDays") + " ≥ " + d.minInactiveDays);
  }
  if (d.minBalance != null) parts.push("≥ " + d.minBalance);
  if (d.maxBalance != null) parts.push("≤ " + d.maxBalance);
  return parts.length ? parts.join(", ") : t("campaign.segmentNoFilter");
}

function describe(err, fallback) {
  if (typeof err === "string" && err.trim()) return err;
  if (err?.message) return err.message;
  return fallback;
}
